// src/components/UpdateNotification.tsx
// PWA update notification to prevent cache-related panic attacks

import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, X } from 'lucide-react';

export default function UpdateNotification() {
  const [showBanner, setShowBanner] = useState(false);

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
      console.warn('SW Registered:', r);
    },
    onRegisterError(error: Error) {
      console.error('SW registration error', error);
    },
  });

  useEffect(() => {
    if (needRefresh) {
      setShowBanner(true);
    }
  }, [needRefresh]);

  const handleUpdate = async () => {
    await updateServiceWorker(true); // true forces the update immediately
    window.location.reload();
  };

  const handleClose = () => {
    setShowBanner(false);
    setNeedRefresh(false);
    setOfflineReady(false);
  };

  // Don't show banner if not needed
  if (!showBanner || (!needRefresh && !offlineReady)) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-phoenix-500 to-orange-600
                 text-white shadow-2xl border-b-4 border-gold-400 animate-slide-down"
      role="alert"
      aria-live="assertive"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Message */}
          <div className="flex items-center gap-3 flex-1">
            <RefreshCw className="w-6 h-6 animate-spin" />
            <div>
              <p className="font-semibold text-lg">
                {needRefresh ? '🎉 New version available!' : 'App ready to work offline'}
              </p>
              <p className="text-sm text-white/90">
                {needRefresh
                  ? 'Click "Update Now" to refresh and load the latest features.'
                  : 'The app is now cached for offline use.'}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {needRefresh && (
              <button
                onClick={handleUpdate}
                className="bg-white text-phoenix-600 px-5 py-2 rounded-lg font-semibold
                         hover:bg-gold-50 transition-colors duration-200 shadow-md
                         flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Update Now
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
              aria-label="Close notification"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-down {
          animation: slide-down 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}
